import {action, makeObservable, observable} from 'mobx';
import {AccountInfo, AuthenticationResult, LogLevel, PublicClientApplication} from '@azure/msal-browser';
import {ApiRepository} from './api-repository';
import * as Sentry from '@sentry/react';

const EESTI_ENERGIA_CLIENT_ID = '4dbe2f0d-cd1c-487f-9784-eefc0c4b8e96';
const TEST_CLIENT_ID = '77eb592a-0871-4978-ada5-f3184339b1ad';
const PROD_CLIENT_ID = 'b15a551c-b9bf-4321-a51a-e35952cec920';

const getClientId = () => {
    const hostname = window.location.hostname;

    return matchDomainToClientId(hostname);
};

const matchDomainToClientId = (hostname: string) => {
    const eestiEnergiaDomains = ['moticheck.energia.ee', 'moticheck-test.energia.ee'];
    const testDomains = ['test.moticheck.com', 'localhost:9000'];

    if (eestiEnergiaDomains.some((domain) => hostname.includes(domain))) {
        return EESTI_ENERGIA_CLIENT_ID;
    } else if (testDomains.some((domain) => hostname.includes(domain))) {
        return TEST_CLIENT_ID;
    } else {
        return PROD_CLIENT_ID;
    }
};

const config = {
    auth: {
        clientId: getClientId(),
        authority: 'https://login.microsoftonline.com/organizations/',
    },
    cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: true,
    },
    system: {
        loggerOptions: {
            loggerCallback: (level: LogLevel, message: string, containsPii: boolean):void => {
                if (containsPii) {
                    return;
                }
                if (level === LogLevel.Verbose) {
                    console.warn(message);

                    return;
                }
                if (level === LogLevel.Info) {
                    console.warn(message);

                    return;
                }
                if (level === LogLevel.Warning) {
                    console.warn(message);

                    return;
                }
                if (level === LogLevel.Error) {
                    console.error(message);
                    Sentry.captureException(message);

                    return;
                }
            },
        },
    },
};

const msalInstance = new PublicClientApplication(config);

const apiConfig = {
    scopes: ['api://5f4e08ca-7d3c-4047-a95e-690298011ffb/access_as_user'],
};

const tokenRequestScopes = [...apiConfig.scopes, 'offline_access','email','profile','openid'];

const AZURE_TENANT_ID = 'azure_tenant_id';

export class AzureSsoStore{

    @observable
    private accessToken: string | null = null;
    @observable
    private userAccount: AccountInfo | null = null;

    constructor(private api: ApiRepository) {
        makeObservable(this);
    }

    @action
    getTenantId(): string | undefined {
        const tenantId = localStorage.getItem(AZURE_TENANT_ID);

        if (tenantId !== null) {
            return tenantId;
        }
    }

    @action
    setTenantId(tenantId: string): void {
        localStorage.setItem(AZURE_TENANT_ID,tenantId);
    }


    @action
    async ssoLogin(tenantId: string): Promise<SsoSessionTokenResponse> {
        this.setTenantId(tenantId);
        const token = await this.azureSsoLogin(tenantId);
        const response = await this.api.post<SsoSessionTokenResponse>(`/azure/user-token`, undefined, { Authorization: token ? 'Bearer '+ token : 'Bearer '});

        if (response.success) {
            return response;
        } else {
            window.alert('It looks like you have signed in to Microsoft with an account that is not matching with your user profile in Moticheck. Kindly sign out from this Microsoft account and try to sign in with a different email address.');
            await this.ssoLogout();

            return { jwt: undefined, success: false};
        }
    }

    @action
    async ssoLogout():Promise<void> {
        const userAccount = this.userAccount;

        localStorage.removeItem(AZURE_TENANT_ID);

        await msalInstance.logoutRedirect(userAccount !== null ? {
            account: msalInstance.getAccountByUsername(userAccount.username),
        } : undefined);
    }

    @action
    async handleMsalRedirectPromise(): Promise<void> {
        const authResponse: AuthenticationResult | null = await msalInstance.handleRedirectPromise();

        if (authResponse !== null) {
            this.accessToken = authResponse.accessToken;
            this.userAccount = authResponse.account;
        }
    }

    private async azureSsoLogin(tenantId: string): Promise<string|undefined> {
        const token = this.accessToken;

        if (token !== null) {
            return token;
        } else {

            await msalInstance.loginRedirect({
                scopes: tokenRequestScopes,
                sid: tenantId,
            });
        }
    }
}

interface SsoSessionTokenResponse{
    jwt?: string,
    success: boolean
}
